'use client';

import { memo, useCallback, useEffect, useRef, useState } from 'react';
import { EdgeToolbar } from '@xyflow/react';
import {
  ArrowLeft,
  ArrowLeftRight,
  ArrowRight,
  Ban,
  ChevronDown,
  CornerDownLeft,
  Files,
  Minus,
  MoreHorizontal,
  MoveRight,
  Spline,
  Trash2,
  Type as TypeIcon,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { haptic } from '@/lib/haptic';
import {
  useWorkspaceActions,
  useWorkspaceItem,
} from '@/components/workspace/store';
import { useWorkspaceSelection } from '@/components/workspace/selection-context';
import { useT } from '@/components/i18n-provider';
import type {
  AnnotationStroke,
  ConnectorArrow,
} from '@/lib/workspaces/types';

/**
 * Figjam-style floating toolbar shown above the selected connector
 * edge. Visually matches `AnnotationToolbar` 1:1 — same chrome, same
 * dropdown primitives, same logical grouping — so the user gets the
 * same UX no matter whether they tap a shape or a line.
 *
 * Layout:
 *
 *   [ Stroke ▼ ] | [ Start ▼ ] [ Path ▼ ] [ End ▼ ] | [ Label ] | [ 🗑 ]
 *
 * - "Stroke" combines style (solid / dashed / dotted / none) — fill
 *   doesn't apply to a 1-D line, so we skip it.
 * - "Start" / "End" pick the arrowhead on each side independently —
 *   matches Figjam's split control (two-way, one-way, no-arrow).
 * - "Path" picks edge geometry (curve / step / straight).
 * - "Label" pops a quick text input.
 * - "Delete" sits in its own group at the right edge.
 *
 * Like `AnnotationToolbar` the container has `pointer-events: none`
 * with `auto` on each descendant button so clicks in the toolbar's
 * empty space pass through to the canvas underneath — keeps edge
 * selection toggling responsive even when two edges share a
 * midpoint zone.
 */

// ----------------------------------------------------------------------
// Static data
// ----------------------------------------------------------------------

// `'none'` is intentionally excluded — an invisible connector with
// no stroke leaves the user with nothing to grab or aim at, so we
// reserve the no-border option for annotation shapes (where the
// fill / label still anchors the hit area) and force connectors to
// stay visible.
const STROKE_OPTIONS: { id: AnnotationStroke; label: string }[] = [
  { id: 'solid', label: 'Solid' },
  { id: 'dashed', label: 'Dashed' },
  { id: 'dotted', label: 'Dotted' },
];

type EdgeShape = 'bezier' | 'smoothstep' | 'straight';

const SHAPE_OPTIONS: { id: EdgeShape; label: string }[] = [
  { id: 'bezier', label: 'Curved' },
  { id: 'smoothstep', label: 'Step' },
  { id: 'straight', label: 'Straight' },
];

// ----------------------------------------------------------------------
// Open-popover state
// ----------------------------------------------------------------------
type OpenPopover =
  | 'stroke'
  | 'start'
  | 'shape'
  | 'end'
  | 'label'
  | null;

function ConnectorEdgeToolbarImpl({
  edgeId,
  itemId,
  centerX,
  centerY,
  selected,
}: {
  edgeId: string;
  itemId: string;
  centerX: number;
  centerY: number;
  selected: boolean;
}) {
  const t = useT();
  const item = useWorkspaceItem(itemId);
  const { patchItemState, removeItem } = useWorkspaceActions();
  const [open, setOpen] = useState<OpenPopover>(null);
  const toolbarRef = useRef<HTMLDivElement | null>(null);
  // Defer to the unified `SelectionToolbar` when multiple items are
  // selected — see annotation-toolbar.tsx for rationale.
  const selectionCount = useWorkspaceSelection().count;

  const cache = item?.state.cache ?? {};
  const stroke = (cache.strokeStyle as AnnotationStroke | undefined) ?? 'solid';
  const arrow = (cache.arrow as ConnectorArrow | undefined) ?? 'arrow';
  // Mirrors the default in `connector-edge.tsx` — smoothstep is the
  // out-of-the-box routing for new connectors so the path is always
  // at strict 90° / 0° angles.
  const edgeShape = (cache.edgeShape as EdgeShape | undefined) ?? 'smoothstep';
  const label = (cache.label as string | undefined) ?? '';

  // Arrow heads are stored as a single field but the user interacts
  // with them as two independent toggles ("start", "end"). We
  // expand the persisted value into two booleans for the toolbar's
  // logic and collapse them back to one of `'none' | 'arrow' |
  // 'arrow-both'` when the user clicks.
  const hasStart = arrow === 'arrow-both';
  const hasEnd = arrow === 'arrow' || arrow === 'arrow-both';

  const setCache = useCallback(
    (patch: Record<string, unknown>) => {
      patchItemState(itemId, { cache: patch }, { debounceMs: 200 });
      haptic('light');
    },
    [itemId, patchItemState],
  );

  const setArrow = useCallback(
    (nextStart: boolean, nextEnd: boolean) => {
      const next: ConnectorArrow =
        nextStart && nextEnd
          ? 'arrow-both'
          : nextEnd
            ? 'arrow'
            : nextStart
              ? // No dedicated "start-only" value — collapse to
                // 'arrow-both' (matches Figjam UX where flipping the
                // start arrow back on while the end was off implies
                // both ends pointable). We could add a 'arrow-start'
                // value later if needed.
                'arrow-both'
              : 'none';
      setCache({ arrow: next });
    },
    [setCache],
  );

  const togglePopover = useCallback((id: NonNullable<OpenPopover>) => {
    setOpen((cur) => (cur === id ? null : id));
  }, []);

  // Outside-click dismiss + Escape — mirrors AnnotationToolbar
  useEffect(() => {
    if (!open) return;
    const onPointer = (e: PointerEvent) => {
      const node = toolbarRef.current;
      if (!node) return;
      if (e.target instanceof Node && node.contains(e.target)) return;
      setOpen(null);
    };
    document.addEventListener('pointerdown', onPointer, true);
    return () => document.removeEventListener('pointerdown', onPointer, true);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(null);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open]);

  if (!selected) return null;
  if (selectionCount > 1) return null;

  return (
    // `alignY="bottom"` anchors the toolbar's BOTTOM edge at the
    // path midpoint so the bar floats ABOVE the line rather than
    // sitting on top of it. This keeps the source / target
    // endpoint dots visible at all times and matches the Figjam /
    // Confluence layout. The `mb-3` on the inner wrapper adds
    // visual breathing room between the bar and the path (in
    // screen-space — the gap stays constant at every zoom level
    // because the EdgeToolbar already cancels the viewport zoom
    // via `scale(1/zoom)`).
    <EdgeToolbar
      edgeId={edgeId}
      x={centerX}
      y={centerY}
      isVisible
      alignY="bottom"
    >
      <div
        ref={toolbarRef}
        className={cn(
          'nodrag nopan select-none',
          'flex items-center gap-0.5 rounded-xl',
          'bg-background/95 backdrop-blur-md',
          'border border-border/70 shadow-lg',
          'px-1 py-1 text-foreground',
          'mb-3',
          // Same pointer-events trick the annotation toolbar uses —
          // empty gaps in the bar let clicks through to the canvas
          // so the user can deselect by tapping nearby.
          '[&_button]:pointer-events-auto pointer-events-none',
        )}
        onPointerDown={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
        onClick={(e) => e.stopPropagation()}
      >
        {/* ================================================================ */}
        {/* Group 1: STROKE STYLE                                            */}
        {/* ================================================================ */}
        <div className="relative">
          <ToolButton
            ariaLabel="Stroke style"
            onClick={() => togglePopover('stroke')}
            active={open === 'stroke'}
            title={t('workspace.toolbar.strokeStyle')}
          >
            <StrokePreview stroke={stroke} />
            <Caret />
          </ToolButton>
          {open === 'stroke' && (
            <Popover label={t('workspace.toolbar.stroke')}>
              <div className="flex flex-col gap-0.5 min-w-[150px]">
                {STROKE_OPTIONS.map((s) => (
                  <MenuItem
                    key={s.id}
                    active={s.id === stroke}
                    onClick={() => {
                      setCache({ strokeStyle: s.id });
                      setOpen(null);
                    }}
                    label={
                      <span className="inline-flex items-center gap-2">
                        <StrokePreview stroke={s.id} />
                        {s.label}
                      </span>
                    }
                  />
                ))}
              </div>
            </Popover>
          )}
        </div>

        {/* ================================================================ */}
        {/* Group 2: ARROW HEADS + PATH SHAPE                                */}
        {/* ================================================================ */}
        <Divider />

        <ToolButton
          ariaLabel="Start arrow"
          onClick={() => setArrow(!hasStart, hasEnd)}
          active={hasStart}
          title={hasStart ? t('workspace.toolbar.removeStartArrow') : t('workspace.toolbar.addStartArrow')}
        >
          <ArrowLeft className="h-3.5 w-3.5" />
        </ToolButton>

        <div className="relative">
          <ToolButton
            ariaLabel="Path shape"
            onClick={() => togglePopover('shape')}
            active={open === 'shape'}
            title={t('workspace.toolbar.pathShape')}
          >
            <PathShapeIcon shape={edgeShape} />
            <Caret />
          </ToolButton>
          {open === 'shape' && (
            <Popover label={t('workspace.toolbar.path')}>
              <div className="flex flex-col gap-0.5 min-w-[140px]">
                {SHAPE_OPTIONS.map((s) => (
                  <MenuItem
                    key={s.id}
                    active={s.id === edgeShape}
                    onClick={() => {
                      setCache({ edgeShape: s.id });
                      setOpen(null);
                    }}
                    label={
                      <span className="inline-flex items-center gap-2">
                        <PathShapeIcon shape={s.id} />
                        {s.label}
                      </span>
                    }
                  />
                ))}
              </div>
            </Popover>
          )}
        </div>

        <ToolButton
          ariaLabel="End arrow"
          onClick={() => setArrow(hasStart, !hasEnd)}
          active={hasEnd}
          title={hasEnd ? t('workspace.toolbar.removeEndArrow') : t('workspace.toolbar.addEndArrow')}
        >
          {hasEnd ? (
            <ArrowRight className="h-3.5 w-3.5" />
          ) : (
            <Minus className="h-3.5 w-3.5" />
          )}
        </ToolButton>

        {/* Both-direction quick toggle — common enough (two-way flow
            diagrams) to deserve a dedicated control rather than two
            taps. */}
        <ToolButton
          ariaLabel="Both arrows"
          onClick={() => setArrow(true, true)}
          active={arrow === 'arrow-both'}
          title={t('workspace.toolbar.twoWay')}
        >
          <ArrowLeftRight className="h-3.5 w-3.5" />
        </ToolButton>

        {/* ================================================================ */}
        {/* Group 3: LABEL                                                   */}
        {/* ================================================================ */}
        <Divider />

        <div className="relative">
          <ToolButton
            ariaLabel="Label"
            onClick={() => togglePopover('label')}
            active={open === 'label' || !!label}
            title={t('workspace.toolbar.label')}
          >
            <TypeIcon className="h-3.5 w-3.5" />
          </ToolButton>
          {open === 'label' && (
            <Popover label={t('workspace.toolbar.label')}>
              <input
                autoFocus
                type="text"
                value={label}
                onChange={(e) => setCache({ label: e.target.value })}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === 'Escape') {
                    setOpen(null);
                  }
                }}
                placeholder={t('workspace.toolbar.addLabel')}
                className={cn(
                  'w-44 rounded-md border border-border/60',
                  'bg-background px-2 py-1 text-sm',
                  'focus:outline-none focus:ring-2 focus:ring-primary/40',
                )}
              />
            </Popover>
          )}
        </div>

        {/* ================================================================ */}
        {/* Group 4: UNIVERSAL EDIT — Duplicate + More + Delete              */}
        {/* ================================================================ */}
        {/*
          Mirror of the trio in `AnnotationToolbar` — same gestures
          (Duplicate / "..." / Delete) so the per-edge toolbar carries
          the universal action set too. The "..." dispatches a global
          event that `workspace-canvas.tsx` listens for and opens the
          unified context menu against the current selection.
        */}
        <Divider />
        <ToolButton
          ariaLabel="Duplicate"
          onClick={() => {
            window.dispatchEvent(
              new CustomEvent('workspace:duplicate-selection'),
            );
          }}
          title="Duplicate (⌘D)"
        >
          <Files className="h-3.5 w-3.5" />
        </ToolButton>
        <ToolButton
          ariaLabel="More actions"
          onClick={() => {
            const wrap = toolbarRef.current;
            if (!wrap) return;
            const rect = wrap.getBoundingClientRect();
            window.dispatchEvent(
              new CustomEvent('workspace:open-more-menu', {
                detail: { x: rect.left, y: rect.bottom + 4 },
              }),
            );
          }}
          title={t('workspace.toolbar.moreActions')}
        >
          <MoreHorizontal className="h-3.5 w-3.5" />
        </ToolButton>
        <Divider />
        <ToolButton
          ariaLabel="Delete edge"
          onClick={() => {
            void removeItem(itemId);
          }}
          danger
          title={t('messages.delete')}
        >
          <Trash2 className="h-3.5 w-3.5" />
        </ToolButton>
      </div>
    </EdgeToolbar>
  );
}

// ----------------------------------------------------------------------
// Sub-primitives — shape matches AnnotationToolbar so both bars read
// as the same control family. Duplicated rather than imported to
// avoid coupling the two files; the styling drift between them is
// expected to be zero and the components are small.
// ----------------------------------------------------------------------

function ToolButton({
  children,
  onClick,
  ariaLabel,
  title,
  active,
  danger,
}: {
  children: React.ReactNode;
  onClick: () => void;
  ariaLabel: string;
  title?: string;
  active?: boolean;
  danger?: boolean;
}) {
  return (
    <button
      type="button"
      aria-label={ariaLabel}
      aria-pressed={active}
      title={title ?? ariaLabel}
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
      className={cn(
        'inline-flex h-7 items-center justify-center gap-1 rounded-md px-2 transition-colors',
        'text-foreground/85 hover:bg-foreground/8 active:bg-foreground/12',
        active && 'bg-primary/12 text-primary',
        danger && 'hover:bg-red-500/10 hover:text-red-600 dark:hover:text-red-400',
      )}
    >
      {children}
    </button>
  );
}

function Caret() {
  return <ChevronDown className="h-3 w-3 opacity-60" />;
}

function Divider() {
  return <span aria-hidden className="mx-0.5 h-5 w-px bg-border/80" />;
}

function MenuItem({
  label,
  active,
  onClick,
}: {
  label: React.ReactNode;
  active?: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
      className={cn(
        'flex items-center justify-between rounded-md px-2 py-1.5 text-left text-sm',
        'hover:bg-foreground/8',
        active && 'bg-foreground/8 font-medium',
      )}
    >
      <span className="inline-flex items-center">{label}</span>
      {active && <span className="text-xs text-muted-foreground">✓</span>}
    </button>
  );
}

function Popover({
  label,
  children,
}: {
  label?: string;
  children: React.ReactNode;
}) {
  return (
    <div
      className={cn(
        'absolute top-[calc(100%+8px)] left-0',
        'glass-panel rounded-lg p-2 shadow-2xl',
        'min-w-max',
        'pointer-events-auto',
        'z-[210]',
      )}
      role="menu"
      onPointerDown={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
      onClick={(e) => e.stopPropagation()}
    >
      {label && (
        <div className="px-1 pb-1.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
          {label}
        </div>
      )}
      {children}
    </div>
  );
}

function StrokePreview({ stroke }: { stroke: AnnotationStroke }) {
  if (stroke === 'none') {
    return <Ban className="h-3.5 w-3.5 text-foreground/55" aria-hidden />;
  }
  return (
    <svg viewBox="0 0 28 6" className="h-1.5 w-7 text-foreground/85" aria-hidden>
      <line
        x1="1"
        y1="3"
        x2="27"
        y2="3"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeDasharray={
          stroke === 'dashed' ? '6 4' : stroke === 'dotted' ? '1 4' : undefined
        }
      />
    </svg>
  );
}

function PathShapeIcon({ shape }: { shape: EdgeShape }) {
  switch (shape) {
    case 'smoothstep':
      // Right-angled step icon — matches Figjam's "step" variant.
      return <CornerDownLeft className="h-3.5 w-3.5" />;
    case 'straight':
      return <MoveRight className="h-3.5 w-3.5" />;
    case 'bezier':
    default:
      return <Spline className="h-3.5 w-3.5" />;
  }
}

export const ConnectorEdgeToolbar = memo(ConnectorEdgeToolbarImpl);
